import React, { useMemo } from 'react';
import FullLayout from 'layout/FullLayout';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { IconBrandWhatsapp, IconClock, IconStar } from '@tabler/icons-react'; // Menggunakan icon dari tabler

import CardClass from 'components/CardClass';

import { API_URL, get } from 'lib/network/api';
import { findStatus } from 'lib/findStatus';
import { AgeDefault } from 'lib/DefaultData';
import { useSelector } from 'react-redux';
import useImageFetcher from 'lib/hooks/useImageFetcher';

const fetchClassDetail = async ({ queryKey }) => {
  const [, { id, hasUser }] = queryKey;
  const url = hasUser
    ? `${API_URL}/api/event/class/${id}?with_prices=true`
    : `${API_URL}/api/event/class/${id}/no-login`;
  const response = await get(url);
  return response;
};

const ViewClass = () => {
  const navigation = useNavigate();
  const { id } = useParams();
  const userProfile = useSelector((state) => state.authProfile);
  const user = userProfile?.data ? userProfile?.data : null;
  const hasUser = !!user;

  const {
    data: classData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['fetchClassDetail', { id, hasUser }],
    queryFn: fetchClassDetail,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const classInfo = classData?.data?.class || {};
  const packages = classData?.data?.packages || [];

  // Mengirimkan array kosong jika images tidak ada agar hook tetap stabil
  const { imageUrls, loading: imageLoading } = useImageFetcher(classInfo.images || [], false);

  const goToSchedule = (paramsItem) => {
    const formatDate = paramsItem?.next_class_date
      ? format(new Date(paramsItem.next_class_date), 'yyyy-MM-dd')
      : format(new Date(), 'yyyy-MM-dd');
    navigation(`/learn/class-schedule?class_id=${paramsItem.id}&date=${formatDate}`);
  };

  const goContactAdmin = () => {
    const phoneNumber = '6281290904616';
    const message = encodeURIComponent(
      `Hi Admin, saya tertarik dengan class "${
        classInfo?.name || 'this class'
      }" dan ingin bertanya lebih lanjut.`,
    );
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const isRegularOrSpecial = ['regular', 'special'].includes(classInfo?.type?.toLowerCase());

  const formattedImages = useMemo(() => {
    const displayImages = imageUrls.length > 0 ? imageUrls : ['/kin/assest/image/icon-profile.png'];

    return displayImages.map((url, index) => ({
      id: index + 1,
      src: url,
      alt: classInfo.name || 'Class image',
    }));
  }, [imageUrls, classInfo.name]);

  if (isLoading)
    return (
      <FullLayout>
        <div className="flex h-[60vh] items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-main"></div>
        </div>
      </FullLayout>
    );

  const durationDisplay = classInfo.duration ? `${classInfo.duration} Mins` : '-';
  const ageGroupDisplay = classInfo.age_group ? findStatus(classInfo.age_group, AgeDefault) : '-';

  return (
    <FullLayout>
      <div>
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-x-12">
            {/* Image Gallery */}
            <TabGroup as="div" className="flex flex-col">
              <TabPanels className="w-full">
                {formattedImages.length > 0 ? (
                  formattedImages.map((image) => (
                    <TabPanel key={image.id}>
                      {/* Container ini yang mengunci ukuran */}
                      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-gray-100 sm:rounded-3xl">
                        <img
                          src={image.src}
                          alt={image.alt}
                          // h-full w-full object-cover memastikan gambar memenuhi kotak tanpa gepeng
                          className="absolute inset-0 h-full w-full object-cover object-center"
                        />
                      </div>
                    </TabPanel>
                  ))
                ) : (
                  /* Placeholder jika gambar kosong agar layout tidak geser */
                  <div className="aspect-[4/3] w-full rounded-2xl bg-gray-200 animate-pulse flex items-center justify-center text-gray-400">
                    No Image Available
                  </div>
                )}
              </TabPanels>

              {/* Thumbnail List - Optimized for Mobile Scroll */}
              {/* Image selector */}
              <div className="mx-auto mt-2 w-full max-w-2xl lg:max-w-none">
                <TabList className="flex flex-row gap-4 overflow-x-auto pb-4 pt-2 px-2 scrollbar-hide focus:outline-none">
                  {imageLoading ? (
                    <div className="flex justify-center items-center h-20 w-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-main"></div>
                    </div>
                  ) : formattedImages.length > 0 ? (
                    formattedImages.map((image) => (
                      <Tab
                        key={image.id}
                        className="group relative flex h-20 w-20 flex-shrink-0 cursor-pointer items-center justify-center rounded-xl focus:outline-none">
                        {({ selected }) => (
                          <>
                            {/* Container Image dengan Padding agar Border tidak terpotong */}
                            <span
                              className={`absolute inset-0 rounded-xl overflow-hidden transition-all duration-200`}>
                              <img
                                alt=""
                                src={image.src}
                                className="h-full w-full object-cover object-center"
                              />
                            </span>

                            {/* Border / Ring yang melingkari dari luar (tidak memotong gambar) */}
                            <span
                              aria-hidden="true"
                              className={`pointer-events-none absolute inset-0 rounded-xl ring-2 transition-all duration-200 ${
                                selected
                                  ? 'ring-primary-main ring-offset-2 scale-100'
                                  : 'ring-transparent group-hover:ring-gray-200'
                              }`}
                            />
                          </>
                        )}
                      </Tab>
                    ))
                  ) : null}
                </TabList>
              </div>
            </TabGroup>

            {/* Content Detail */}
            <div className="mt-8 px-0 sm:mt-16 sm:px-0 lg:mt-0">
              <div className="flex flex-col border-b border-gray-100 pb-6">
                <span className="text-sm font-bold tracking-widest text-primary-main uppercase">
                  {classInfo.subject || 'CLASS'}
                </span>
                <h1 className="text-xl font-extrabold tracking-tight text-kin-text sm:text-2xl">
                  {classInfo.name}
                </h1>
                <p className="text-lg text-gray-600 italic">{classInfo.theme}</p>

                <div className="flex flex-wrap gap-4 pt-2">
                  <div className="flex items-center gap-1.5 border border-kin-text px-3 py-1 rounded-full">
                    <IconStar size={16} className="text-yellow-500" />
                    <span className="text-sm font-medium">{ageGroupDisplay}</span>
                  </div>
                  <div className="flex items-center gap-1.5 border border-kin-text px-3 py-1 rounded-full">
                    <IconClock size={16} className="text-blue-500" />
                    <span className="text-sm font-medium">{durationDisplay}</span>
                  </div>
                </div>
              </div>

              {/* Description Sections */}
              <div className="mt-6 space-y-8 divide-y divide-gray-100">
                <section>
                  <h3 className="text-sm font-bold text-kin-text mb-2">ABOUT THIS CLASS</h3>
                  <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                    {classInfo.description || 'No description available'}
                  </div>
                </section>

                <section className="pt-4">
                  <h3 className="text-sm font-bold text-kin-text mb-2">WHAT YOU WILL EXPERIENCE</h3>
                  <div className="text-sm text-gray-600 leading-relaxed">
                    {classInfo.what_to_expect || 'Information coming soon'}
                  </div>
                </section>

                <section className="pt-4">
                  <h3 className="text-sm font-bold text-kin-text mb-2">THINGS TO BRING</h3>
                  <div className="text-sm text-gray-600 leading-relaxed">
                    {classInfo.requirements || 'No special requirements'}
                  </div>
                </section>
              </div>

              {/* Packages Section */}
              {hasUser && packages.length > 0 && isRegularOrSpecial && (
                <div className="mt-8 border-t border-gray-100 pt-8">
                  <h3 className="text-sm font-bold text-kin-text mb-4">AVAILABLE PACKAGES</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {packages.map((pkg) => (
                      <CardClass key={pkg.id} item={pkg} context="detail" hideBuyButton />
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                {isRegularOrSpecial ? (
                  <button
                    onClick={() => goToSchedule(classInfo)}
                    className="flex w-full items-center justify-center rounded-full border border-primary-main bg-white px-8 py-3 text-sm font-bold text-primary-main hover:bg-primary-main hover:text-white transition-all sm:w-auto">
                    VIEW SCHEDULE
                  </button>
                ) : (
                  <button
                    onClick={goContactAdmin}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-8 py-3 text-sm font-bold text-white hover:bg-[#20BA5A] transition-all sm:w-auto">
                    <IconBrandWhatsapp size={20} />
                    CONTACT VIA WHATSAPP
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </FullLayout>
  );
};

export default ViewClass;
